import { useCallback, useState } from 'react';
import { OperationData } from '../../../types/OperationData';
import { telegramApi } from '../../../utils/telegramApi';

/**
 * Хук для управления коллекцией фотографий.
 * 
 * Ответственность:
 * - CRUD операции с фотографиями (добавление, удаление, выбор)
 * - Управление режимами просмотра (съемка/просмотр)
 * - Контроль лимитов количества фотографий
 * - Навигация между фотографиями
 * 
 * @param operationData - данные операции для контроля лимитов
 * @returns Состояние коллекции и методы управления
 */
export function usePhotoCollection(operationData: OperationData | null) {
    const [images, setImages] = useState<string[]>([]);
    const [selectedPhoto, setSelectedPhoto] = useState<string>('');

    /**
     * Добавляет новое изображение в коллекцию.
     * 
     * Автоматически переводит в режим просмотра только при достижении
     * максимального количества фотографий согласно операционным данным.
     */
    const addImage = useCallback((imageSrc: string) => {
        const newImages = [...images, imageSrc];
        setImages(newImages);
        
        // Переходим в режим просмотра только при достижении максимума фотографий
        const maxImages = operationData?.max_images || 10;
        if (newImages.length >= maxImages) {
            setSelectedPhoto(imageSrc);
        }
        
        return newImages;
    }, [images, operationData]);

    /**
     * Переключает приложение в режим просмотра выбранной фотографии.
     */
    const selectImage = useCallback((image: string) => {
        setSelectedPhoto(image);
    }, []);

    /**
     * Удаляет фотографию из списка после подтверждения пользователя.
     */
    const deleteImage = useCallback((index: number) => {
        telegramApi.showConfirm('Удалить это фото?').then((confirmed) => {
            if (confirmed) {
                const deletedPhoto = images[index];
                setImages(prev => prev.filter((_, i) => i !== index));
                
                // Если удаляем выбранное фото, нужно выбрать другое
                if (deletedPhoto === selectedPhoto) {
                    const newImages = images.filter((_, i) => i !== index);
                    if (newImages.length > 0) {
                        setSelectedPhoto(newImages[0]);
                    } else {
                        setSelectedPhoto('');
                    }
                }
            }
        });
    }, [images, selectedPhoto]);

    /**
     * Возвращает приложение в режим съемки из режима просмотра.
     */
    const clearSelection = useCallback(() => {
        setSelectedPhoto('');
    }, []);

    const canAddMore = images.length < (operationData?.max_images || 10);

    return {
        images,
        selectedPhoto,
        addImage,
        selectImage,
        deleteImage,
        clearSelection,
        canAddMore
    };
}